import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { currencyFormatter } from '../../../services/helperService';
import { InventorySectionProduct, StdUnit, AdditionalCountUnit } from '../../../services/inventory/inventoryService';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sectionId: string;
  product: InventorySectionProduct | null;
}

export interface Preset {
  unit: string;
  factor: number;
}

interface UnitRow {
  unit: string;
  /** The number in the factor input. Canonical count→std factor when `inverse` is false;
   *  the flipped reading ("1 <stdUnit> = N <unit>") when true — canonical = 1/factor. */
  factor: number;
  /** Input direction only (piece products) — the persisted value is always count→std. */
  inverse: boolean;
  isDefault: boolean;
}

/** Count units offered per std unit. Shared with the inline unit column on the edit page. */
export const PRESETS_BY_STD: Record<StdUnit, Preset[]> = {
  g: [
    { unit: 'kg', factor: 1000 },
    { unit: 'g', factor: 1 },
  ],
  ml: [
    { unit: 'L', factor: 1000 },
    { unit: 'ml', factor: 1 },
    { unit: 'fles', factor: 750 },
    { unit: 'doos', factor: 9000 },
  ],
  // Bulk presets for piece products start at factor 1 — the user sets the real
  // pack size (e.g. tray of 24) like any custom unit.
  p: [
    { unit: 'p', factor: 1 },
    { unit: 'doos', factor: 1 },
    { unit: 'tray', factor: 1 },
    { unit: 'pack', factor: 1 },
  ],
  unit: [
    { unit: 'unit', factor: 1 },
    { unit: 'doos', factor: 1 },
    { unit: 'tray', factor: 1 },
    { unit: 'pack', factor: 1 },
  ],
};

/** Canonical count→std factor for a row, whatever direction the input shows. */
const toStdFactor = (u: Pick<UnitRow, 'factor' | 'inverse'>): number => (u.inverse ? 1 / u.factor : u.factor);

/** Round an inverted factor so 1/(1/750) re-displays as 750, not 750.0000000001. */
const roundFactor = (v: number): number => Math.round(v * 1e6) / 1e6;

export default function EditProductUnitModal({ isOpen, onClose, sectionId, product }: Props) {
  const { t } = useTranslation();
  const { updateProductUnits } = useInventoryStore();
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !product) return;
    // Cross-dimension units on piece products store fractional factors (1 p = 750 g
    // ⇒ factor 1/750); open those in the inverse direction so the user sees 750.
    const isPiece = product.stdUnit === 'p' || product.stdUnit === 'unit';
    const toRow = (unit: string, factor: number, isDefault: boolean): UnitRow => {
      const inverse = isPiece && factor > 0 && factor < 1;
      return { unit, factor: inverse ? roundFactor(1 / factor) : factor, inverse, isDefault };
    };
    const primary = toRow(product.countUnit, product.countUnitToStdFactor, true);
    const extras = (product.additionalCountUnits || []).map(u => toRow(u.unit, u.factor, false));
    setUnits([primary, ...extras]);
    setSearch('');
    setError(null);
    setSaving(false);
  }, [isOpen, product]);

  const presets = useMemo(() => {
    if (!product) return [];
    return PRESETS_BY_STD[product.stdUnit] || [];
  }, [product]);

  const filteredUnits = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return units.map((u, idx) => ({ unit: u, idx }));
    return units
      .map((u, idx) => ({ unit: u, idx }))
      .filter(({ unit }) => unit.unit.toLowerCase().includes(q));
  }, [units, search]);

  if (!isOpen || !product) return null;

  const updateUnit = (idx: number, patch: Partial<UnitRow>) => {
    setUnits(prev => prev.map((u, i) => (i === idx ? { ...u, ...patch } : u)));
  };

  const setDefault = (idx: number) => {
    setUnits(prev => prev.map((u, i) => ({ ...u, isDefault: i === idx })));
  };

  const removeUnit = (idx: number) => {
    setUnits(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== idx);
      if (!next.some(u => u.isDefault)) next[0] = { ...next[0], isDefault: true };
      return next;
    });
  };

  const addUnit = (preset: Preset) => {
    setUnits(prev => {
      const exists = prev.some(u => u.unit.trim().toLowerCase() === preset.unit.toLowerCase());
      if (exists) return prev;
      return [...prev, { unit: preset.unit, factor: preset.factor, inverse: false, isDefault: false }];
    });
  };

  const addCustomUnit = () => {
    setUnits(prev => [...prev, { unit: '', factor: 1, inverse: false, isDefault: false }]);
  };

  /** Flip a row's input direction, keeping the physical conversion the same. */
  const toggleDirection = (idx: number) => {
    setUnits(prev => prev.map((u, i) => {
      if (i !== idx) return u;
      const flipped = Number.isFinite(u.factor) && u.factor > 0 ? roundFactor(1 / u.factor) : u.factor;
      return { ...u, inverse: !u.inverse, factor: flipped };
    }));
  };

  const validate = (): { primary: AdditionalCountUnit; additional: AdditionalCountUnit[] } | null => {
    if (units.length === 0) {
      setError(t('inventory.unitNeedAtLeastOne') as string);
      return null;
    }
    const seen = new Set<string>();
    for (const u of units) {
      const name = u.unit.trim();
      if (!name) {
        setError(t('inventory.unitInvalidName') as string);
        return null;
      }
      if (!Number.isFinite(u.factor) || u.factor <= 0) {
        setError(t('inventory.unitInvalidFactor') as string);
        return null;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        setError(t('inventory.unitDuplicate', { unit: name }) as string);
        return null;
      }
      seen.add(key);
    }
    const def = units.find(u => u.isDefault) ?? units[0];
    const additional: AdditionalCountUnit[] = units
      .filter(u => u !== def)
      .map(u => ({ unit: u.unit.trim(), factor: toStdFactor(u) }));
    return {
      primary: { unit: def.unit.trim(), factor: toStdFactor(def) },
      additional,
    };
  };

  const handleSave = async () => {
    setError(null);
    const valid = validate();
    if (!valid) return;
    setSaving(true);
    try {
      await updateProductUnits(product.id, sectionId, {
        countUnit: valid.primary.unit,
        countUnitToStdFactor: valid.primary.factor,
        additionalCountUnits: valid.additional,
      });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update units');
      setSaving(false);
    }
  };

  const presetsAvailableToAdd = presets.filter(
    p => !units.some(u => u.unit.trim().toLowerCase() === p.unit.toLowerCase())
  );

  // Piece products get the direction toggle (cross-dimension units like "1 p = 750 g").
  const isPieceProduct = product.stdUnit === 'p' || product.stdUnit === 'unit';

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('inventory.editUnit')}
      subtitle={product.productName}
      closeLabel={t('inventory.cancel') as string}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={slideOverCancelClass}>
            {t('inventory.cancel')}
          </button>
          <button type="button" onClick={handleSave} disabled={saving} className={slideOverPrimaryClass}>
            {saving ? t('inventory.loading') : t('inventory.save')}
          </button>
        </>
      }
    >
      {product.weightString && (
        <p className="text-[14px] sm:text-[16px] leading-5 text-[#667085] dark:text-[#CBD5E1]">
          {t('inventory.soldAs')}: {product.weightString}
        </p>
      )}

      {error && (
        <div className="mb-4 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      <p className="text-[14px] sm:text-[16px] leading-[20px] font-semibold text-[#344054] dark:text-zinc-50 mb-3 sm:mb-4 mt-5 sm:mt-6">
        {t('inventory.countUnits')}
      </p>

      <div className="relative w-full mb-5">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085] dark:text-[#CBD5E1]" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('inventory.searchCountUnits') as string}
          className="w-full pl-10 pr-4 h-11 rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] text-[16px] leading-6 font-normal placeholder:text-[#667085] dark:placeholder:text-[#CBD5E1] text-zinc-900 dark:text-zinc-50 focus:outline-none"
        />
      </div>

      <div className='border border-[#E8E8E9] dark:border-[#2D4F68] rounded-lg p-3 sm:p-4'>
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-3 max-h-[calc(100vh-430px)]">
          {filteredUnits.length === 0 ? (
            <p className="text-[14px] text-[#667085] dark:text-zinc-400 text-center py-6">—</p>
          ) : filteredUnits.map(({ unit: u, idx }) => {
            const stdFactor = Number.isFinite(u.factor) && u.factor > 0 ? toStdFactor(u) : null;
            const previewPricePerCountUnit = product.pricePerStdUnit && stdFactor !== null ? product.pricePerStdUnit * stdFactor : null;
            const unitLabel = u.unit.trim() || t('inventory.unit');

            return (
              <div
                key={idx}
                className={cn(
                  'p-3 rounded-xl border space-y-3 transition-colors',
                  u.isDefault
                    ? 'border-[#0E8965] dark:border-green-700 bg-[#0E89651A] dark:bg-green-900/10'
                    : 'border-[#E8E8E9] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42]'
                )}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={u.unit}
                    onChange={e => updateUnit(idx, { unit: e.target.value })}
                    placeholder={t('inventory.unitNamePlaceholder') as string}
                    className="flex-1 min-w-0 px-3 h-10 rounded-lg border border-[#D0D5DD] dark:border-[#1E3A4E] bg-white dark:bg-[#081C2C] text-[14px] leading-5 font-semibold placeholder:text-[#344054] text-[#101828] dark:text-zinc-50 focus:outline-none disabled:opacity-60 shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
                  />
                  <div className='flex items-center gap-2'>
                    <button
                      type="button"
                      onClick={() => setDefault(idx)}
                      title={t('inventory.setAsDefault') as string}
                      className={cn(
                        'h-10 w-10 flex items-center justify-center rounded-lg border transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040] ',
                        u.isDefault
                          ? 'border-[#0E8965] bg-white dark:bg-[#081C2C] text-[#0E8965] dark:text-darkGreenColor-dark'
                          : 'border-[#D0D5DD] dark:border-[#1E3A4E] bg-white dark:bg-[#081C2C]'
                      )}
                      aria-label={t('inventory.setAsDefault') as string}
                    >
                      <svg className={cn('sm:w-[22px] sm:h-[22px] w-[18px] h-[18px]', u.isDefault && 'fill-current dark:fill-current')} viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.4796 2.19418L14.2394 5.74288C14.4794 6.23687 15.1193 6.7107 15.6592 6.80143L18.8489 7.33575C20.8887 7.67853 21.3686 9.17059 19.8988 10.6425L17.419 13.1427C16.9991 13.5661 16.7691 14.3827 16.8991 14.9675L17.609 18.0625C18.169 20.5123 16.8791 21.46 14.7293 20.1796L11.7397 18.3952C11.1997 18.0726 10.3098 18.0726 9.75989 18.3952L6.77022 20.1796C4.63046 21.46 3.3306 20.5022 3.89054 18.0625L4.60046 14.9675C4.73045 14.3827 4.50047 13.5661 4.08052 13.1427L1.60079 10.6425C0.140953 9.17059 0.610902 7.67853 2.65068 7.33575L5.84032 6.80143C6.37026 6.7107 7.01019 6.23687 7.25017 5.74288L9.00997 2.19418C9.96987 0.268607 11.5297 0.268607 12.4796 2.19418Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeUnit(idx)}
                      disabled={units.length <= 1}
                      className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#D0D5DD] dark:border-[#1E3A4E] bg-white dark:bg-[#081C2C] text-[#D92D20] dark:text-[#F87171] disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
                      aria-label={t('inventory.removeUnit') as string}
                    >
                      <svg className='sm:w-6 sm:h-6 w-5 h-5 dark:text-[#F87171] text-[#D92D20]' viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.5 5.5L18.8803 15.5251C18.7219 18.0864 18.6428 19.3671 18.0008 20.2879C17.6833 20.7431 17.2747 21.1273 16.8007 21.416C15.8421 22 14.559 22 11.9927 22C9.42312 22 8.1383 22 7.17905 21.4149C6.7048 21.1257 6.296 20.7408 5.97868 20.2848C5.33688 19.3626 5.25945 18.0801 5.10461 15.5152L4.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M3 5.5H21M16.0557 5.5L15.3731 4.09173C14.9196 3.15626 14.6928 2.68852 14.3017 2.39681C14.215 2.3321 14.1231 2.27454 14.027 2.2247C13.5939 2 13.0741 2 12.0345 2C10.9688 2 10.436 2 9.99568 2.23412C9.8981 2.28601 9.80498 2.3459 9.71729 2.41317C9.32164 2.7167 9.10063 3.20155 8.65861 4.17126L8.05292 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M9.5 16.5L9.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M14.5 16.5L14.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[14px] leading-5 text-[#667085] font-medium dark:text-[#CBD5E1] whitespace-nowrap">
                    1 {u.inverse ? product.stdUnit : unitLabel} =
                  </span>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="any"
                    value={Number.isFinite(u.factor) ? u.factor : ''}
                    onChange={e => updateUnit(idx, { factor: Number(e.target.value) })}
                    className="w-20 px-3 h-10 sm:h-8 rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-zinc-50 focus:outline-none"
                  />
                  <span className="text-[14px] leading-5 font-medium text-[#667085] dark:text-[#CBD5E1]">{u.inverse ? unitLabel : product.stdUnit}</span>
                  {isPieceProduct && (
                    <button
                      type="button"
                      onClick={() => toggleDirection(idx)}
                      title={t('inventory.unitSwapDirection') as string}
                      aria-label={t('inventory.unitSwapDirection') as string}
                      className="h-10 w-10 sm:h-8 sm:w-8 flex items-center justify-center rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] text-[#344054] dark:text-zinc-200 hover:bg-gray-50 transition-colors"
                    >
                      <ArrowLeftRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {previewPricePerCountUnit !== null && (
                    <span className="text-[14px] leading-5 font-medium text-[#667085] dark:text-[#CBD5E1] ml-auto">
                      {currencyFormatter.format(previewPricePerCountUnit)} / {unitLabel}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          <div className="">
            <p className="text-[14px] leading-[25px] font-medium text-[#344054] dark:text-[#CBD5E1] mb-3">{t('inventory.addUnit')}</p>
            <div className="flex flex-wrap gap-3">
              {presetsAvailableToAdd.map(p => (
                <button
                  key={p.unit}
                  type="button"
                  onClick={() => addUnit(p)}
                  className="px-2.5 py-[5px] rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68] text-[14px] font-medium text-[#344054] dark:text-zinc-200 hover:bg-gray-50 dark:bg-[#14344C] flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {p.unit}
                </button>
              ))}
              <button
                type="button"
                onClick={addCustomUnit}
                className="px-2.5 py-[5px] rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68] text-[14px] leading-5 font-semibold text-[#344054] dark:text-zinc-200 hover:bg-gray-50 dark:bg-[#14344C] flex items-center gap-1 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                {t('inventory.customUnit')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </InventorySlideOver>
  );
}
